import useMobileVersion from '@otovo/shared/lib/useMobileVersion';
import {
  Sanity$AboutUsDocument,
  Sanity$CampaignPage,
  Sanity$HomePage,
  Sanity$PackagePage,
  Sanity$PricingPage,
} from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import { stegaClean } from 'next-sanity';
import { ConfigurablePortableText } from '../../Sanity/PortableTextComponents';
import AddressSearchBar from '../AddressSearchBar';
import Banner from './Banner';
import TrustVendor from './TrustVendor';
import HeroImage from './HeroImage';

type Props = {
  page:
    | Sanity$HomePage
    | Sanity$PackagePage
    | Sanity$PricingPage
    | Sanity$CampaignPage
    | Sanity$AboutUsDocument;
};

const HeroSection = ({ page }: Props) => {
  const image = page.heroSection.mainImage;
  const isMobile = useMobileVersion(640);
  const { headline, lead, keyValuePropositions, banner } = page.heroSection;
  const hasAddressBar = stegaClean(page.heroSection.addressBarToggle);
  const mainHardwareType =
    ('mainHardwareType' in page && page.mainHardwareType) || 'pv_system';

  const showBanner = banner?.content || banner?.cta;

  return (
    <div className="relative flex min-h-screen w-full items-end sm:pr-24 md:py-24 md:pr-0 lg:py-32 2xl:min-h-[80vh]">
      <div className="isolate">
        {image?.asset && <HeroImage image={image} />}
        {showBanner && <Banner banner={banner} />}

        {/* Gradient */}
        <div
          style={{
            background: isMobile
              ? 'linear-gradient(rgba(13, 18, 28, 0.05) 34.58%, rgba(13, 18, 28, 0.6) 57.08%, rgba(13, 18, 28, 0.8) 69.71%)'
              : 'linear-gradient(270deg, rgba(248, 248, 252, 0) 13.89%, rgba(16, 25, 50, 0.45) 50%, rgba(13, 18, 28, 0.7) 100%)',
          }}
          className="absolute inset-0 z-10"
        />
      </div>
      {/* Content displayed on top of the hero image */}
      <div className="isolate mx-4 mt-auto h-[25rem] w-full max-w-8xl sm:mx-0 sm:h-[32rem] xl:h-[36rem] 2xl:mx-auto 2xl:px-16">
        <div
          className={cx(
            'grid h-full w-full text-white',
            // [Key value propositions] + [headline + lead + address bar] + [trust vendor]
            'grid-rows-[4rem_12rem_4rem]',
            'sm:grid-cols-12 sm:grid-rows-[4rem_19rem_4rem]',
            'md:mt-12 lg:mt-24',
            'xl:grid-rows-[4rem_21rem_4rem]',
            'z-20',
          )}
        >
          {/* Key Value Propositions */}
          <section className="row-start-1 sm:col-span-6 sm:!col-start-2 md:col-span-8 2xl:!col-start-1">
            {page.heroSection.keyValuePropositionTitle &&
              keyValuePropositions?.length > 0 && (
                <div className="flex items-start gap-6">
                  <h2 className="sr-only">
                    {page.heroSection.keyValuePropositionTitle}
                  </h2>
                  {keyValuePropositions.map((proposition) => (
                    <div key={proposition.title}>
                      <h3 className="font-semibold text-shadow-2xs text-shadow-black">
                        {proposition.title}
                      </h3>
                      <p className="text-xs font-medium text-shadow-2xs text-shadow-black">
                        {proposition.subtitle}
                      </p>
                    </div>
                  ))}
                </div>
              )}
          </section>

          {/* Headline + Lead */}
          <section className="row-start-2 sm:col-span-6 sm:!col-start-2 md:col-span-5 2xl:!col-start-1">
            <h1 className="text-3xl font-semibold text-balance text-shadow-2xs text-shadow-black md:text-4xl lg:text-5xl">
              {headline}
            </h1>
            <div className="mt-2 overflow-hidden">
              {lead && (
                <ConfigurablePortableText
                  value={lead}
                  components={{
                    block: {
                      normal: ({ children: textChildren }) => (
                        <p className="text-sm font-normal text-shadow-2xs text-shadow-black md:text-lg xl:text-xl">
                          {textChildren}
                        </p>
                      ),
                    },
                  }}
                />
              )}
            </div>
            {/* Address Search Bar */}
            <section
              id="nav-scroll-trigger"
              className="mt-3 text-black sm:mt-6"
            >
              {hasAddressBar && (
                <AddressSearchBar mainHardwareType={mainHardwareType} />
              )}
            </section>
          </section>

          {/* Trust Vendor */}
          <section className="row-start-3 mt-16 self-start pb-2 sm:col-span-6 sm:!col-start-2 sm:mt-24 md:col-span-5 lg:mt-12 2xl:!col-start-1">
            {page.heroSection.trustVendor && (
              <TrustVendor {...page.heroSection.trustVendor} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
